import { Component, PureComponent, useMemo } from 'react'
import _ from 'lodash'
import cx from 'classnames'
import PropTypes from 'prop-types'
import {
  TimePicker as AntdTimePicker,
  DatePicker,
  Radio,
  Checkbox,
  InputNumber as AntdInputNumber,
  Input as AntdInput
} from 'antd'
import moment from 'moment'
import { Icon, MenuSelect, DatePicker as SharedDatePicker } from '@vzmi/shp_shared_compo'
import RFSelect from './RFSelect'
import Flatpickr, { WrappedFlatpickr } from './YFlatpickr'
import { eventPreventDefault, getDefaultTimezoneMoment, getUuid } from '../../../lib/renderUtil'
import { DATE_FORMAT } from '../../lib/MomentHelper'

import styles from './Form.css'
import { isEmptyStr } from '../../lib/Validator'
import SingleLineText from '../Proposal/Common/ReadOnlyComponents/SingleLineText/SingleLineText'
import { removeNonPrintableCharParser } from '../../lib/parser'
import CheckboxMenuSelect from './CheckboxMenuSelect'
import MassiveMenuSelect from './MassiveMenuSelect'

const AntdRadioGroup = Radio.Group
const AntdCheckboxGroup = Checkbox.Group
const AntdRangePicker = DatePicker.RangePicker

const preventAndTriggerEvent = triggerEvent => e => {
  eventPreventDefault(e)
  triggerEvent && triggerEvent(e)
}

export const HyperLink = ({
  className,
  href,
  children,
  target,
  rel = '',
  isButton,
  ...props
}) => {
  const clz = cx(styles.HyperLink, {
    [className]: !!className,
    [styles.isButton]: isButton
  })
  return (
    <a
      className={clz}
      href={href}
      target={target}
      rel={`${rel} ${target === '_blank' && 'noopener noreferrer'}`}
      {...props}
    >
      {children}
    </a>
  )
}

export class RadioButton extends Component {
  handleSwitchOnChange = (e) => {
    const { onChange } = this.props
    const value = e.target.value
    onChange && onChange(value, e)
  }

  render () {
    const { className, id, name, text, children, value, checked = false, color } = this.props

    const clz = cx(styles.radioButton, {
      [className]: !!className,
      [styles[color]]: !!color
    })

    const elementId = id || name
    return (
      <span className={clz}>
        <input
          type='radio'
          id={elementId}
          name={name}
          value={value || (checked)}
          checked={checked}
          onChange={this.handleSwitchOnChange}
        />
        <label htmlFor={elementId}>{text || children}</label>
      </span>
    )
  }
}

export const RadioGroup = ({
  className,
  buttonStyle = 'outline',
  ...props
}) => {
  const dataRole = _.get(props, 'data-role', '')
  const clz = cx(
    styles.RadioGroup,
    { [className]: !!className }
  )

  return (
    <div
      data-role={dataRole}
      className={clz}
    >
      <AntdRadioGroup
        buttonStyle={buttonStyle}
        optionType={buttonStyle === 'solid' ? 'button' : 'default'}
        {...props}
      />
    </div>
  )
}

const convertFromOptionValue = (inputValue, optionValue) => {
  const actualType = typeof inputValue
  const expectType = typeof optionValue
  if (expectType === actualType) {
    return inputValue
  }

  if (expectType === 'string' && actualType === 'number') {
    return _.toString(inputValue)
  }

  if (expectType === 'number' && actualType === 'string') {
    return _.toNumber(inputValue)
  }

  if (expectType === 'boolean' && actualType === 'string') {
    if (inputValue === 'true') {
      return true
    }

    if (inputValue === 'false') {
      return false
    }
  }

  return inputValue
}

export const RadioGroupInput = ({
  children,
  buttonStyle = 'outline',
  input,
  onChange,
  onBlur,
  readOnly,
  options,
  ...props
}) => {
  // 輸入值以 option value 為基準改掉輸入的型態
  // 避免 option value 為數字但是表單經過轉換後輸入進來變成字串
  // 會造成 antd radio 判斷形態不同導致沒有選項被勾取以及後續取值資料型態不同
  const optionValue = useMemo(() => _.get(options, '0.value'), [options])
  const rootValue = convertFromOptionValue(input.value, optionValue)
  const convertedInput = {
    ...input,
    value: rootValue
  }
  return (
    <RadioGroup
      {...convertedInput}
      {...props}
      options={options}
      readOnly={readOnly}
      buttonStyle={buttonStyle}
      optionType={buttonStyle === 'solid' ? 'button' : 'default'}
      onChange={e => {
        const value = convertFromOptionValue(_.get(e, 'target.value'), optionValue)
        if (value !== rootValue) {
          input.onChange(value)
          onChange && onChange(value)
        }
      }}
      onBlur={e => {
        const value = convertFromOptionValue(_.get(e, 'target.value'), optionValue)
        if (value !== rootValue) {
          input.onBlur(value)
          onBlur && onBlur(value)
        }
      }}
    />
  )
}

export const RadioGroupInputField = withFormError(RadioGroupInput)

export const AntdCheckbox = ({ className, type, label, checked, children, value, ...props }) => {
  const clz = cx(styles.AntdCheckbox, {
    [className]: !!className,
    [styles[type]]: !!type
  })
  const dataRole = _.get(props, 'data-role', '')
  return (
    <div
      className={clz}
      data-role={`${dataRole}-checkbox`}
    >
      <Checkbox
        {...props}
        value={value}
        checked={checked}
      >
        {label || children}
      </Checkbox>
    </div>
  )
}

export const CheckboxInput = ({
  input,
  onChange,
  readOnly,
  disabled,
  ...props
}) => {
  const isChecked = _.has(props, 'checked') ? props.checked : !!input.value
  return (
    <AntdCheckbox
      disabled={readOnly || disabled}
      {...input}
      {...props}
      input={input}
      checked={isChecked}
      onChange={(e) => {
        const value = _.get(e, 'target.checked', e)
        // support without redux form
        input && input.onChange(value)
        onChange && onChange(value)
      }}
    />
  )
}

export const CheckboxInputField = withFormError(CheckboxInput)

export const CheckboxGroup = props => {
  const omittedProps = _.omit(props, 'showFormError')
  const {
    className,
    input,
    onChange,
    ...restProps
  } = omittedProps
  const clz = cx(styles.CheckboxGroup, {
    [className]: !!className
  })
  const dataRole = _.get(restProps, 'data-role', '')
  return (
    <div
      className={clz}
      data-role={`${dataRole}-checkbox-group`}
    >
      <AntdCheckboxGroup
        onChange={value => {
          input.onChange(value)
          onChange && onChange(value)
        }}
        {...restProps}
        value={input.value}
      />
    </div>
  )
}

export const CheckboxGroupField = withFormError(CheckboxGroup)

const BaseInputNumber = ({ className, input, ...props }) => {
  const clz = cx(styles.InputNumber, {
    [className]: !!className
  })
  const defaultDataRole = 'input-number'
  const dataRole = props['data-role']
  return (
    <div
      data-role={dataRole ? `${dataRole}-${defaultDataRole}` : defaultDataRole}
      className={clz}
    >
      <AntdInputNumber
        {...props}
      />
    </div>
  )
}

export const InputNumber = ({ input, parser, min, max, allowNegative, ...props }) => {
  const localParser = (value = '') => {
    const parserRegex = allowNegative ? /[^-\d+]/g : /[^\d+]/g
    const localValue = `${value}`.replace(parserRegex, '')
    return parser ? parser(localValue) : localValue
  }
  const onChange = (value = '') => {
    let val = value
    if ((val !== input.value)) {
      if (min && _.toNumber(val) < min) {
        val = min
      }
      if (max && _.toNumber(val) > max) {
        val = max
      }
      input.onChange(localParser(val))
    }
  }
  return (
    <BaseInputNumber
      {...input}
      {...props}
      min={min}
      max={max}
      parser={localParser}
      onBlur={() => input.onBlur()}
      onChange={onChange}
    />
  )
}

export const InputNumberField = withFormError(withInputPlaceholder(InputNumber))

export const CheckboxMenuSelectInput = (props) => {
  const { input, normalizeCustomInput } = props
  const { value = [], onChange, onBlur } = input
  const normalizeCustomInputFunc = normalizeCustomInput
    ? _.flow([removeNonPrintableCharParser, normalizeCustomInput])
    : removeNonPrintableCharParser

  return (
    <CheckboxMenuSelect
      {...props}
      normalizeCustomInput={normalizeCustomInputFunc}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
    />
  )
}

export const CheckboxMenuSelectField = withFormError(CheckboxMenuSelectInput)

export const MassiveMenuSelectInput = (props) => {
  const { input } = props
  const { value, onChange, onBlur } = input

  return (
    <MassiveMenuSelect
      {...props}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
    />
  )
}

export const MassiveMenuSelectField = withFormError(MassiveMenuSelectInput)

export class TimePicker extends Component {
  getDisabledHours = () => {
    const props = this.props
    const { value, minDate, maxDate } = props
    const minDateMoment = moment(minDate)
    const maxDateMoment = moment(maxDate)
    const currentDate = value || moment()
    let range = []
    if (minDate) {
      const minDuration = moment.duration(currentDate.diff(minDateMoment))
      const minDurationHours = minDuration.asHours()
      const minTimeHours = minDateMoment.get('hours')
      if ((minDurationHours + minTimeHours) < 23 || (minDurationHours <= 1 && minDateMoment.isSameOrBefore(currentDate))) {
        range = _.range(0, minTimeHours)
      }
    }

    if (maxDate) {
      const maxDuration = moment.duration(currentDate.diff(maxDateMoment))
      const maxDurationHours = maxDuration.asHours()
      const maxTimeHours = maxDateMoment.get('hours')
      if (maxDurationHours > 0) {
        return _.range(0, 24)
      }

      if (maxDurationHours <= 1) {
        range = [...range, ..._.range(maxTimeHours + 1, 24)]
      }
    }
    return range
  }

  getDisabledMinutes = (selectedHour) => {
    const props = this.props
    const { value, minDate, maxDate } = props
    const minDateMoment = moment(minDate)
    const maxDateMoment = moment(maxDate)
    const currentDate = value || moment()
    let range = []
    if (minDate) {
      const minDuration = moment.duration(currentDate.diff(minDateMoment))
      const minDurationMinutes = minDuration.asMinutes()
      const minTimeMinutes = minDateMoment.get('minutes')
      if (minDurationMinutes < minTimeMinutes && minDateMoment.isSameOrBefore(currentDate)) {
        range = _.range(0, minTimeMinutes)
      }
    }
    if (maxDate) {
      const maxDuration = moment.duration(currentDate.diff(maxDateMoment))
      const maxDurationMinutes = maxDuration.asMinutes()
      const maxTimeMinutes = maxDateMoment.get('minutes')
      if (maxDurationMinutes > 0) {
        return _.range(0, 60)
      }
      if (maxDurationMinutes <= 1) {
        range = [...range, ..._.range(maxTimeMinutes + 1, 60)]
      }
    }
    return range
  }

  render () {
    const props = this.props
    const {
      size = 'large',
      getPopupContainer,
      format = 'HH:mm',
      placeholder = '請選擇',
      readOnly
    } = props
    return (
      <span className={styles.TimePicker}>
        <div
          key='container'
          className={styles.TimePickerContainer}
          ref={r => (this.timePickerContainer = r)}
        />
        <AntdTimePicker
          {...props}
          key='picker'
          size={size}
          format={format}
          placeholder={placeholder}
          disabledHours={this.getDisabledHours}
          disabledMinutes={this.getDisabledMinutes}
          getPopupContainer={getPopupContainer || (() => this.timePickerContainer)}
          popupClassName={cx(styles.TimePickerPopup, 'time-picker-popup', props.popupClassName)}
          disabled={readOnly}
        />
      </span>
    )
  }
}

export class DateTimePicker extends Component {
  componentDidMount () {
    const { input, onChange } = this.props
    const { value: inputValue } = input
    input.onChange(inputValue)
    onChange && onChange(inputValue)
  }

  handleChange = dates => {
    const { input, onChange } = this.props
    input.onChange(dates)
    onChange && onChange(dates)
  }

  render () {
    const {
      input,
      meta,
      onChange,
      displayFormat,
      ...restProps
    } = this.props
    return (
      <SharedDatePicker
        onChange={this.handleChange}
        defaultValue={input.value}
        format={displayFormat}
        {...restProps}
      />
    )
  }
}

export const DateTimePickerField = withFormError(DateTimePicker)

export class RangePicker extends Component {
  componentDidMount () {
    const { input, onChange } = this.props
    const { value: inputValue } = input
    input.onChange(inputValue)
    onChange && onChange(inputValue)
  }

  handleChange = dates => {
    const { input, onChange } = this.props
    input.onChange(dates)
    onChange && onChange(dates)
  }

  // 打開清空redux 日期，若沒有選擇日期就關閉選單，則帶回前一次日期
  onOpenChange = open => {
    const { input, meta } = this.props
    const { value: inputValue } = input
    // 瀏覽量分析比較日期區間的時候，redux initialValues初始為空，避免被判斷當前值為空，帶回預設空值
    if (_.isEmpty(meta.initial)) {
      return
    }
    if (open) {
      input.onChange([null, null])
    } else {
      const currentValIsEmpty = _.every(inputValue, _.isNull)
      if (currentValIsEmpty) {
        input.onChange(meta.initial)
      }
    }
  }

  render () {
    const {
      className,
      input,
      meta,
      onChange,
      'data-role': dataRole,
      ...restProps
    } = this.props
    return (
      <div data-role={dataRole}>
        <AntdRangePicker
          placeholder={['開始日期', '結束日期']}
          separator=''
          format={DATE_FORMAT.UI}
          className={cx(styles.RangePicker, className)}
          onChange={this.handleChange}
          onOpenChange={this.onOpenChange}
          defaultValue={meta.initial}
          // suffixIcon={<CalendarIcon input={input} {...restProps} />}
          {...restProps}
        />
      </div>
    )
  }
}

export const RangePickerField = withFormError(RangePicker)

export const TimePickerInput = (props) => {
  const {
    input,
    allowEmpty = true
  } = props
  const {
    value = moment(),
    onChange,
    onBlur
  } = input

  const momentValue = moment.isMoment(value)
    ? value
    : moment(value)

  const localValue = momentValue.isValid()
    ? momentValue
    : null

  return (
    <TimePicker
      {...props}
      allowEmpty={allowEmpty}
      value={localValue}
      onBlur={() => onBlur()}
      onChange={(nextMoment) => {
        const { minDate, maxDate } = props
        const minDateMoment = moment(minDate)
        const maxDateMoment = moment(maxDate)
        if (allowEmpty && !nextMoment) {
          onChange('')
        } else if (nextMoment) {
          if (minDate && minDateMoment.isValid() && nextMoment.isSameOrBefore(minDateMoment)) {
            onChange(minDateMoment.set('seconds', 0).format())
          } else if (maxDate && nextMoment.isSameOrAfter(maxDateMoment)) {
            onChange(maxDateMoment.set('seconds', 0).format())
          } else {
            nextMoment && nextMoment.isValid() && onChange(nextMoment.set('seconds', 0).format())
          }
        }
      }}
    />
  )
}

export const TimePickerField = withFormError(TimePickerInput)

export const FormLabel = ({
  align = 'baseline',
  style,
  children,
  className,
  tooltip = {},
  icon,
  iconType,
  withSuffix,
  suffix
}) => {
  const {
    component: Tooltip,
    props: tooltipProps = {}
  } = tooltip

  const iconInstance = icon ||
   (iconType && (
     <Icon
       name={iconType}
       size={14}
       align={align}
       color='#979ba7'
     />
   )) ||
   null

  const renderIcon = iconInstance && (
    <span
      key='icon'
      className={styles.icon}
    >
      {iconInstance}
    </span>
  )

  return (
    <div
      className={cx(
        styles.FormLabel,
        className,
        { [styles.withIcon]: !!renderIcon }
      )}
    >
      {Tooltip
        ? (
          <Tooltip {...tooltipProps}>
            {children}
            {renderIcon}
          </Tooltip>
        )
        : (
          <>
            {children}
            {renderIcon}
          </>
        )}
      {withSuffix && suffix}
    </div>
  )
}

export const FormRow = (props) => {
  const {
    withInputCountPaddingBottom,
    withTopTip,
    labelAlign,
    subTitle,
    subTitleColor,
    noLabel,
    label,
    style,
    children,
    className,
    contentClassName,
    withoutMargin,
    withoutPaddingRight
  } = props || {}
  const labelClass = {
    [styles[labelAlign]]: !!labelAlign
  }
  const dataRole = props['data-role']
  return (
    <div
      className={cx(styles.FormRow, {
        [className]: !!className,
        [styles.withInputCountPaddingBottom]: !!withInputCountPaddingBottom,
        [styles.withTopTip]: withTopTip
      })}
      data-role={dataRole}
      style={style}
    >
      {!noLabel && (
        <label
          className={cx({
            [styles.withoutMargin]: withoutMargin,
            [styles.withoutPaddingRight]: withoutPaddingRight
          })}
        >
          <div
            className={cx(styles.title, labelClass)}
          >
            {label}
            {subTitle && (
              <div
                className={
                  cx(styles.subTitle, {
                    [styles[subTitleColor]]: !!subTitleColor
                  })
                }
              >
                {subTitle}
              </div>
            )}
          </div>
        </label>
      )}
      <div className={cx(styles.content, contentClassName)}>
        {children}
      </div>
    </div>
  )
}

export const FormError = ({
  showFormError,
  error = [],
  inline,
  absolute
} = {}) => {
  const localErrors = Array.isArray(error)
    ? error
    : [error]
  const hasError = !_.isEmpty(error)
  const errorLength = _.size(localErrors)

  return showFormError && hasError
    ? _.map(
      localErrors,
      (error, idx) => {
        return (
          <div
            key={idx}
            data-role='help-block'
            className={cx(
              styles.helpBlock,
              styles.withErrors,
              {
                [styles.absolute]: absolute,
                'help-block-error': hasError,
                'has-form-error': hasError,
                [styles.inline]: inline,
                [styles.list]: errorLength > 1
              }
            )}
          >
            {error}
          </div>
        )
      }
    )
    : null
}

export function withFormError (Component) {
  return class extends PureComponent {
    render () {
      const {
        meta,
        className,
        hasFormErrorTag = true,
        inlineError,
        absoluteError,
        mustShowFormError,
        showErrorOnActive = false,
        showErrorAfterSubmitted = false,
        hideErrorOnEmptyInput = false,
        keepErrorPosition = false,
        alwaysIgnoreError = false,
        ...restProps
      } = this.props
      const { touched, error, dirty, active, invalid, submitFailed } = meta
      const isEmptyError = isEmptyStr(error)
      const isEmptyInputValue = isEmptyStr(_.get(this.props, 'input.value', ''))
      let showFormError = !isEmptyError && (
        !isEmptyInputValue ||
        (
          showErrorOnActive
            ? ((dirty && active) || mustShowFormError)
            // 一般欄位
            : (dirty || touched || invalid)
        )
      )
      if (
        // 如果欄位為空，而且還沒操作過，就不顯示錯誤
        (hideErrorOnEmptyInput && isEmptyInputValue && !touched) ||
        alwaysIgnoreError
      ) {
        showFormError = false
      }

      // 送出才顯示錯誤訊息
      if (showErrorAfterSubmitted) {
        // 要顯示錯誤訊息且送出失敗
        showFormError = (showFormError && submitFailed)
      }
      const classes = cx(className, { 'has-form-error': showFormError })
      return (
        <>
          <Component
            key='component'
            className={classes}
            meta={meta}
            showFormError={showFormError}
            {...restProps}
          />
          {hasFormErrorTag && (
            <FormError
              showFormError={showFormError}
              key={'error'}
              inline={inlineError}
              absolute={absoluteError}
              {...meta}
            />
          )}
          {(!showFormError && keepErrorPosition) && (
            <div
              className={cx(
                styles.helpBlock,
                styles.emptyBlock
              )}
            />
          )}
        </>
      )
    }
  }
}

class TextareaInputField extends PureComponent {
  render () {
    const { input, textarea, disabled, className, placeholder, rows } = this.props
    const restProps = {
      disabled,
      placeholder
    }
    if (rows) {
      restProps.rows = rows
    }

    const dataRole = this.props['data-role']
    return (
      <div
        data-role={dataRole}
        className={cx(styles.TextareaInputField, { [className]: !!className })}
      >
        <textarea
          {...input}
          {...textarea}
          {...restProps}
          data-role='textarea-field'
          // prevent redux form re-render
          onBlur={preventAndTriggerEvent(input.onBlur)}
          onFocus={preventAndTriggerEvent(input.onFocus)}
        />
      </div>
    )
  }
}

export const TextareaField = withFormError(TextareaInputField)
export const TextareaCountField = withFormError(withInputCount(TextareaInputField))

export class BaseAntdInputField extends PureComponent {
  render () {
    const {
      input,
      placeholder,
      type = 'text',
      disabled,
      readOnly,
      className,
      addonAfter,
      addonBefore
    } = this.props

    const dataRole = this.props['data-role']
    return (
      <div
        data-role={dataRole}
        className={cx(styles.InputField, className, { [styles.disabled]: disabled })}
      >
        <AntdInput
          autoComplete='off'
          {...input}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          type={type}
          data-role='input-field'
          addonAfter={addonAfter}
          addonBefore={addonBefore}
          // prevent redux form re-render
          onBlur={preventAndTriggerEvent(input.onBlur)}
          onFocus={preventAndTriggerEvent(input.onFocus)}
        />
      </div>
    )
  }
}

export const InputField = withFormError(BaseAntdInputField)
export const InputCountField = withFormError(withInputCount(BaseAntdInputField))
export class RFSelectInput extends PureComponent {
  render () {
    const { className, children, ...restProps } = this.props
    return (
      <>
        <RFSelect
          {...restProps}
          key='RFSelectInput'
          className={cx(styles.RFSelectInput, { [className]: !!className })}
        />
        {children}
      </>
    )
  }
}
export const RFSelectInputField = withFormError(RFSelectInput)

export class FlatpickrInput extends PureComponent {
  render () {
    const { input, name, id, className, meta, children, onClose, options = {}, ...restProps } = this.props
    const { onChange, onBlur, value } = input
    return (
      <>
        <Flatpickr
          {...input}
          {...restProps}
          options={{
            ...options,
            parseDate: (dateString, format) => {
              const timezonedDate = getDefaultTimezoneMoment(dateString)

              return new Date(
                timezonedDate.year(),
                timezonedDate.month(),
                timezonedDate.date(),
                timezonedDate.hour(),
                timezonedDate.minute(),
                timezonedDate.second()
              )
            }
          }}
          key='FlatpickrInput'
          className={cx(styles.FlatpickrInput, { [className]: !!className })}
          id={id}
          name={name}
          value={value || ''}
          onBlur={() => onBlur()}
          onClose={(selectedDates, dateStr, instance) => {
            if (!value) {
              const selectedDate = !selectedDates[0] ? (restProps.options.defaultDate || new Date()) : selectedDates[0]
              onChange(selectedDate)
            }
            onClose(selectedDates, dateStr, instance)
          }}
          onChange={(selectedDates, dateStr, instance) => {
            const finalDates = selectedDates
            if (instance.config.mode === 'range') {
              if (finalDates.length === 1) {
              /** if it's range mode, it shouldn't be allowed to empty the selection,
               * and this is meant to restore the time
               *
               * make sure the added time is not overlapping with maxDate or it will fail */
                onChange([...finalDates, new Date()])
              } else {
                onChange(finalDates)
              }
            } else {
              const selectedDate = !finalDates[0] ? (options.defaultDate || new Date()) : finalDates[0]
              onChange(selectedDate)
              onBlur()
            }
            if (this.props.onChange) {
              this.props.onChange(selectedDates, dateStr, instance)
            }
          }}
        >
          {children}
        </Flatpickr>
      </>
    )
  }
}

export class FlatpickrTimeInput extends PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      isOpen: false
    }
  }

  handleOpen = (selectedDates, dateStr, instance) => {
    this.setState({
      isOpen: true
    })
  }

  handleClose = (selectedDates, dateStr, instance) => {
    this.setState({
      isOpen: false
    })
  }

  render () {
    const { children, className, options = {}, ...restProps } = this.props
    const omittedRestProps = _.omit(restProps, 'showFormError')
    const { isOpen } = this.state
    const clz = cx(styles.FlatpickrTimeInput, {
      [className]: !!className,
      [styles.isOpen]: isOpen
    })
    return (
      <>
        <FlatpickrInput
          {...omittedRestProps}
          className={clz}
          data-role='flatpickr-time-input'
          options={{
            ...options,
            wrap: true
          }}
          onOpen={this.handleOpen}
          onClose={this.handleClose}
        >
          <input
            type='text'
            name={restProps.input.name}
            placeholder={restProps.placeholder || '請選擇日期...'}
            data-input
          />
          <label
            data-toggle
            className={styles.arrowZone}
            onClick={this.handleClick}
          >
            <span className={styles.arrow} />
          </label>
        </FlatpickrInput>
      </>
    )
  }
}
export const FlatpickrTimeInputField = withFormError(FlatpickrTimeInput)

export class WrappedFlatpickrInput extends Component {
  render () {
    const {
      replaceTime,
      defaultValue,
      input,
      name,
      id,
      className,
      meta,
      children,
      options,
      textOnly,
      ...restProps
    } = this.props

    const omittedRestProps = _.omit(restProps, 'showFormError')
    if (!_.isEmpty(options)) {
      _.merge(omittedRestProps, { options })
    }
    const {
      onChange,
      onBlur,
      value,
      name: inputName
    } = input

    const propsOnChange = this.props.onChange

    if (textOnly) {
      return (
        <>
          <span name={inputName}>{getDefaultTimezoneMoment(value).format(DATE_FORMAT.UI)}</span>
          {children}
        </>
      )
    }

    // 用options來指定key讓Flatpickr可以套用minDate的更新，否則會認為options沒有變動則不重新render
    return (
      <>
        <WrappedFlatpickr
          {...omittedRestProps}
          key={getUuid(options)}
          className={cx(styles.WrappedFlatpickrInput, { [className]: !!className })}
          id={id}
          value={value}
          name={name || input.name}
          onBlur={() => onBlur()}
          onClose={(selectedDates, dateStr, instance) => {
            if (instance.config.mode === 'range') {
              if (selectedDates.length === 1) {
                /** if it's range mode, it shouldn't be allowed to empty the selection,
             * and this is meant to restore the time
             *
             * make sure the added time is not overlapping with maxDate or it will fail */
                onChange([...selectedDates, new Date()])
              } else {
                onChange(selectedDates)
              }
            } else {
              const localValue = selectedDates[0]
              if (!localValue) {
                onChange(null)
                if (propsOnChange) {
                  propsOnChange(null, null, instance)
                }
                return
              }
              let valueMoment = getDefaultTimezoneMoment(value)
              const selectedDateMoment = getDefaultTimezoneMoment(localValue).set({ hour: 0, minute: 0, second: 0 })
              const defaultDate = options.defaultDate
              const defaultDateMoment = getDefaultTimezoneMoment(defaultDate)
              if (selectedDateMoment.isBefore(defaultDateMoment)) {
                valueMoment = defaultDateMoment
              }
              if (replaceTime) {
                selectedDateMoment.hours(valueMoment.hours()).minutes(valueMoment.minutes())
              }
              const finalValue = selectedDateMoment.format()
              onChange(finalValue)
            }
            if (propsOnChange) {
              propsOnChange(selectedDates, dateStr, instance)
            }
          }}
        />
        {children}
      </>
    )
  }
}

export const WrappedFlatpickrInputField = withFormError(WrappedFlatpickrInput)

export function withInputCount (Component) {
  // eslint-disable-next-line
  class CountWrapper extends Component {
    static propTypes = {
      countLimit: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]),
      countShow: PropTypes.bool
    }

    static defaultProps = {
      countLimit: 0,
      countShow: true
    }

    getCount = () => {
      const { input, value = '', customizeGetCount } = this.props
      if (customizeGetCount) {
        return customizeGetCount(this.props)
      }
      let inputValue = input.value || ''
      inputValue = (Array.isArray(inputValue) ? _.get(inputValue, 0, '') : inputValue) || ''
      // value 是數字時不能算長度，必須轉成字串
      return (this.component && _.get(this.component, 'value', '').length) || `${inputValue}`.length || `${value}`.length || 0
    }

    isExceedLimit = (count) => {
      const { countLimit, countLessEqual } = this.props
      return countLessEqual ? (countLimit && count >= countLimit) : (countLimit && count > countLimit)
    }

    renderCount = (count, isExceedLimit) => {
      const { countLimit, countClass } = this.props
      const className = cx(styles.count, {
        countClass,
        [styles.exceedLimit]: isExceedLimit
      })

      return (
        <div
          data-role='with-input-count-counting'
          key='counting'
          className={className}
        >
          {countLimit >= 0 ? `(${count}/${countLimit})` : count }
        </div>
      )
    }

    render () {
      const {
        showFormError,
        countLimit,
        countShow,
        fullWidth,
        inlineCount,
        className,
        children,
        ...restProps
      } = this.props
      const count = this.getCount()
      const isExceedLimit = this.isExceedLimit(count)
      const hasError = showFormError || isExceedLimit
      const classes = cx(
        styles.WithInputCount, {
          [styles.fullWidth]: fullWidth,
          [styles.inlineCount]: inlineCount,
          [styles.countError]: hasError
        }
      )
      return (
        <>
          <div
            data-role='with-input-count'
            className={classes}
          >
            <Component
              key='component'
              ref={(elem) => (this.component = elem)}
              className={cx(className, {
                [styles.fullWidth]: fullWidth,
                [styles.countError]: hasError
              })}
              showFormError={showFormError}
              {...restProps}
            />
            {countShow && this.renderCount(count, hasError)}
          </div>
          {children}
        </>
      )
    }
  }
  return CountWrapper
}

export function withInputPlaceholder (Component) {
  // eslint-disable-next-line
  return class extends PureComponent {
    render () {
      const { subPlaceholder, ...restProps } = this.props
      return (
        <span className={styles.withInputPlaceholder}>
          <Component
            key='component'
            {...restProps}
          />
          {subPlaceholder && <div className={styles.subPlaceholder}>{subPlaceholder}</div>}
        </span>
      )
    }
  }
}

export const When = ({ value, is, isTrue, isFalse, orElse = null, children = null }) => ((isTrue && value === isTrue) || (isFalse && value === !isFalse) || (is && value === is) ? children : orElse)

const defaultCustomLabel = (options, value) => {
  const label = _.flow(
    () => _.find(options, { value }),
    option => _.get(option, 'label', '')
  )()
  return label
}

class MenuSelectInput extends PureComponent {
  handleMenuSelect = (option, menuIndex, e) => {
    const { value: optVal } = option
    const { input, customUpdateValue = v => v } = this.props
    const { onChange, onBlur } = input
    if (_.isEmpty(optVal)) {
      onChange(null)
      onBlur(null)
    } else {
      onChange(customUpdateValue([optVal]))
      onBlur(customUpdateValue([optVal]))
    }
  }

  handleCustomInputSubmit = (inputValue, foundInputOption) => {
    const inputValOpt = { value: removeNonPrintableCharParser(inputValue) }
    this.handleMenuSelect(inputValOpt)
  }

  handlePopupVisibleChange = (popupVisible) => {
    if (popupVisible === false) {
      const { input } = this.props
      const { onBlur } = input
      onBlur()
    }
  }

  // 如果 options { value, label } 都是相同值就不需要這個資料轉換
  // 但如果 { value, label } 為不同值要顯示在 select input 上出現 label 就要打開 showOptionLabel flag
  getInputPickerValue = () => {
    const {
      showOptionLabel,
      customLabel = defaultCustomLabel,
      input,
      options
    } = this.props
    const value = _.get(input, 'value', '')
    if (!showOptionLabel) {
      return value
    }

    const label = customLabel(options, value)
    return label
  }

  render () {
    const { input, normalizeCustomInput, customInputValue = v => v, ...restProps } = this.props
    const { value: originValue } = input
    const value = customInputValue ? customInputValue(originValue) : originValue
    const normalizeCustomInputFunc = normalizeCustomInput ? _.flow([removeNonPrintableCharParser, normalizeCustomInput]) : removeNonPrintableCharParser
    const arrayValue = _.isEmpty(value) ? [] : value
    return (
      <MenuSelect
        {...restProps}
        value={arrayValue}
        inputPickerValue={this.getInputPickerValue()}
        onCustomInputSubmit={this.handleCustomInputSubmit}
        normalizeCustomInput={normalizeCustomInputFunc}
        onPopupVisibleChange={this.handlePopupVisibleChange}
        onSelect={this.handleMenuSelect}
      />
    )
  }
}
export const MenuSelectField = withFormError(MenuSelectInput)

export const SingleLineTextField = withFormError(SingleLineText)
